import type { AnalyticsResolution, AnonymousTicketSummary, OperationalTimelineResponse } from "@queueryte/contracts";

export interface LinePaths { line: string; top: string; band: string; }
export interface JourneyRow {
  ticket: AnonymousTicketSummary;
  waitLeft: number;
  waitWidth: number;
  serviceLeft: number;
  serviceWidth: number;
  overTarget: boolean;
  label: string;
}
export interface JourneyLayout {
  start: number;
  end: number;
  rows: JourneyRow[];
  /** Tickets left waiting or in service when the business day closed. Their end is unknown, so they are drawn as a marker only. */
  unresolved: number;
  breaks: Array<{ left: number; width: number }>;
  hours: Array<{ left: number; label: string }>;
}

const durationSteps = [2, 4, 6, 10, 20, 30, 40, 60, 90, 120, 180, 240, 360, 480, 720, 1440];
const countSteps = [2, 4, 6, 8, 10, 20, 30, 40, 50, 60, 80, 100, 150, 200, 300, 400, 500, 1000, 2000, 5000];

export const columnCenter = (index: number, total: number): number => total <= 0 ? 0 : (index + 0.5) / total * 100;

/** Axis maximum in seconds, rounded up to a whole number of minutes that halves cleanly. */
export function niceDurationMax(seconds: number): number {
  const minutes = Math.max(0, seconds) / 60;
  return (durationSteps.find((step) => step >= minutes) ?? Math.ceil(minutes / 1440) * 1440) * 60;
}

export function niceCountMax(value: number): number {
  return countSteps.find((step) => step >= value) ?? Math.ceil(value / 5000) * 5000;
}

export function preciseDuration(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value) || value < 0) return "-";
  const total = Math.round(value);
  if (total < 60) return `${total}s`;
  const minutes = Math.floor(total / 60), seconds = total % 60;
  if (minutes >= 60) return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, "0")}m`;
  return seconds === 0 ? `${minutes}m` : `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

/** Median line, upper line, and the band between them. A null median breaks the line instead of estimating. */
export function linePaths(median: Array<number | null>, upper: Array<number | null>, max: number): LinePaths {
  const total = median.length, y = (value: number) => (100 - Math.min(max, Math.max(0, value)) / Math.max(1, max) * 100).toFixed(2);
  const at = (index: number, value: number, first: boolean) => `${first ? "M" : "L"}${columnCenter(index, total).toFixed(2)} ${y(value)}`;
  const result: LinePaths = { line: "", top: "", band: "" };
  let segment: number[] = [];
  const flush = () => {
    if (segment.length === 0) return;
    result.line += segment.map((index, position) => at(index, median[index] ?? 0, position === 0)).join(" ") + " ";
    const banded = segment.filter((index) => upper[index] !== null && upper[index] !== undefined);
    if (banded.length === segment.length) {
      const top = segment.map((index, position) => at(index, upper[index] ?? 0, position === 0)).join(" ");
      result.top += top + " ";
      if (segment.length > 1) result.band += `${top} ${[...segment].reverse().map((index) => at(index, median[index] ?? 0, false)).join(" ")} Z `;
    }
    segment = [];
  };
  median.forEach((value, index) => { if (value === null || value === undefined) flush(); else segment.push(index); });
  flush();
  return result;
}

export function stepPaths(values: number[], max: number): { line: string; area: string } {
  if (values.length === 0) return { line: "", area: "" };
  const y = (value: number) => (100 - Math.min(max, Math.max(0, value)) / Math.max(1, max) * 100).toFixed(2);
  const line = values.map((value, index) => `${index === 0 ? "M" : "L"}${(index / values.length * 100).toFixed(2)} ${y(value)} L${((index + 1) / values.length * 100).toFixed(2)} ${y(value)}`).join(" ");
  return { line, area: `${line} L100 100 L0 100 Z` };
}

export const resolutionMilliseconds = (resolution: AnalyticsResolution): number => resolution === "15m" ? 900_000 : resolution === "1d" ? 86_400_000 : 3_600_000;

export function intervalLabel(bucketStart: string, resolution: AnalyticsResolution): string {
  const start = new Date(bucketStart);
  if (resolution === "1d") return start.toLocaleDateString([], { weekday: "short", day: "numeric", month: "short" });
  const time = (value: Date) => value.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  return `${time(start)}-${time(new Date(start.getTime() + resolutionMilliseconds(resolution)))}`;
}

/** One row per ticket with a reliable issue time: the wait, then the service, as percentages of the business day. */
export function journeyLayout(data: OperationalTimelineResponse, now: number): JourneyLayout | null {
  const start = data.businessStartedAt ? Date.parse(data.businessStartedAt) : Number.NaN;
  if (!Number.isFinite(start)) return null;
  const reportedEnd = data.businessEndedAt ? Date.parse(data.businessEndedAt) : Number.NaN;
  const end = Math.max(start + 60_000, data.isOpen || !Number.isFinite(reportedEnd) ? now : reportedEnd);
  const span = end - start, position = (value: number) => Math.min(100, Math.max(0, (value - start) / span * 100));
  const time = (value: number) => new Date(value).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
  const rows = data.tickets.flatMap((ticket): JourneyRow[] => {
    if (ticket.issuedAt === null) return [];
    const issued = Date.parse(ticket.issuedAt), called = ticket.calledAt === null ? null : Date.parse(ticket.calledAt);
    const completed = ticket.completedAt === null ? null : Date.parse(ticket.completedAt);
    // Only an open day can extend an unfinished ticket to "now"; after closing, how long it really lasted is unknown.
    const waitEnd = called ?? (ticket.status === "waiting" && data.isOpen ? end : issued);
    const serviceEnd = called === null ? null : completed ?? (ticket.status === "serving" && data.isOpen ? end : called);
    const waitSeconds = ticket.waitSeconds ?? (waitEnd - issued) / 1000;
    const overTarget = ticket.slaMet === false || (ticket.slaMet === null && ticket.status === "waiting" && waitSeconds > data.slaTargetSeconds);
    const state = ticket.status !== "waiting" ? `waited ${preciseDuration(ticket.waitSeconds)}` : data.isOpen ? `waiting ${preciseDuration(waitSeconds)}` : "not called before the business day closed";
    const service = ticket.status === "completed" ? `, service ${preciseDuration(ticket.serviceSeconds)}` : ticket.status !== "serving" ? "" : data.isOpen ? ", in service" : ", no recorded completion";
    return [{
      ticket, overTarget,
      waitLeft: position(issued), waitWidth: Math.max(0.3, position(waitEnd) - position(issued)),
      serviceLeft: called === null ? 0 : position(called),
      serviceWidth: called === null || serviceEnd === null ? 0 : Math.max(0.3, position(serviceEnd) - position(called)),
      label: `Ticket ${ticket.localTicketId.slice(0, 8)}: issued ${time(issued)}, ${state}${service}`,
    }];
  }).sort((left, right) => left.waitLeft - right.waitLeft);
  const breaks = data.breaks.flatMap((item) => {
    const left = position(Date.parse(item.startedAt)), right = position(Date.parse(item.endedAt));
    return right > left ? [{ left, width: right - left }] : [];
  });
  const hours: JourneyLayout["hours"] = [], step = span > 12 * 3_600_000 ? 2 : 1, first = new Date(start);
  first.setMinutes(0, 0, 0);
  for (let value = first.getTime() + 3_600_000; value < end - span * 0.04; value += step * 3_600_000) {
    if (value - start > span * 0.03) hours.push({ left: position(value), label: time(value) });
  }
  const unresolved = data.isOpen ? 0 : rows.filter((row) => row.ticket.status !== "completed").length;
  return { start, end, rows, unresolved, breaks, hours };
}
